import { useState } from 'react';
import { academyPrograms, academyCategories, locations } from '../data/academyPrograms';

const Academy = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Filter programs by category
  const filteredPrograms = selectedCategory === 'All' 
    ? academyPrograms 
    : academyPrograms.filter(program => program.category === selectedCategory);

  // Get featured programs for hero section
  const featuredPrograms = academyPrograms.filter(program => program.featured);

  return (
    <main className="min-h-screen bg-ballet-light">
      {/* Hero Section */}
      <div className="bg-elegant-gradient text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-5xl lg:text-6xl font-elegant font-bold mb-6">
            NB Academy
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto">
            History, Tradition, Excellence - Professional dance training for all ages
          </p>
        </div>
      </div>

      {/* Featured About Sections */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {featuredPrograms.map((program) => (
            <div key={program.id} className="card-elevated overflow-hidden">
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-3 h-3 bg-ballet-burgundy rounded-full"></div>
                  <span className="text-sm font-medium text-ballet-burgundy uppercase tracking-wide">
                    {program.category}
                  </span>
                </div>
                
                <h2 className="text-3xl font-elegant font-bold text-ballet-burgundy mb-3">
                  {program.title}
                </h2>
                
                <h3 className="text-xl text-ballet-gold font-semibold mb-6">
                  {program.subtitle}
                </h3>
                
                <p className="text-gray-700 leading-relaxed text-lg">
                  {program.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <h2 className="text-4xl font-elegant font-bold gradient-text text-center mb-8">
            Our Programs & Services
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {academyCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-ballet-burgundy text-white shadow-lg transform scale-105'
                    : 'bg-white text-ballet-burgundy hover:bg-ballet-light border border-ballet-burgundy/20'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredPrograms.filter(program => !program.featured).map((program) => (
            <div key={program.id} className="card-elevated overflow-hidden group hover:shadow-2xl transition-all duration-300">
              {/* Program Image Placeholder */}
              <div className="h-48 bg-soft-gradient flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-ballet-burgundy/10 group-hover:bg-ballet-burgundy/20 transition-all duration-300"></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-ballet-burgundy rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <span className="text-ballet-burgundy font-semibold">{program.category}</span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-block px-3 py-1 bg-ballet-light text-ballet-burgundy text-sm font-medium rounded-full">
                    {program.category}
                  </span>
                  {program.ageRange && (
                    <span className="text-sm text-gray-600 font-medium">
                      {program.ageRange}
                    </span>
                  )}
                </div>

                <h3 className="text-xl font-elegant font-bold text-ballet-burgundy mb-2 group-hover:text-ballet-gold transition-colors duration-200">
                  {program.title}
                </h3>

                <h4 className="text-ballet-gold font-semibold mb-3">
                  {program.subtitle}
                </h4>

                <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                  {program.description}
                </p>

                {/* Highlights */}
                {program.highlights && (
                  <div className="mb-4">
                    <ul className="space-y-1">
                      {program.highlights.slice(0, 3).map((highlight, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-ballet-burgundy rounded-full mr-2"></div>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Locations */}
                {program.locations && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {program.locations.map((location) => (
                        <span key={location} className="inline-block px-2 py-1 bg-ballet-burgundy/10 text-ballet-burgundy text-xs font-medium rounded">
                          {location}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dress Code */}
                {program.dressCodes && (
                  <div className="mb-4 p-3 bg-ballet-light rounded-lg">
                    <h5 className="text-sm font-semibold text-ballet-burgundy mb-2">Dress Code:</h5>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div><strong>Girls:</strong> {program.dressCodes.girls}</div>
                      <div><strong>Boys:</strong> {program.dressCodes.boys}</div>
                    </div>
                  </div>
                )}

                {/* Call to Action */}
                <div className="flex space-x-3">
                  <button className="flex-1 btn-primary text-sm py-2">
                    {program.callToAction || 'Learn More'}
                  </button>
                  <button className="px-4 py-2 bg-ballet-light text-ballet-burgundy rounded-lg hover:bg-ballet-pink transition-colors duration-200 text-sm font-medium">
                    Info
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Locations Section */}
        <div className="bg-white rounded-2xl shadow-elegant p-8 mb-16">
          <h2 className="text-3xl font-elegant font-bold gradient-text text-center mb-8">
            Our Locations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {locations.map((location) => (
              <div key={location.name} className="bg-soft-gradient p-6 rounded-xl">
                <h3 className="text-xl font-bold text-ballet-burgundy mb-4">
                  {location.name} Location
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-ballet-burgundy mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-gray-800 font-medium">{location.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-ballet-burgundy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a href={`tel:${location.phone}`} className="text-ballet-burgundy hover:text-ballet-gold transition-colors duration-200 font-medium">
                      {location.phone}
                    </a>
                  </div>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button className="btn-primary text-sm">
                    Get Directions
                  </button>
                  <button className="btn-outline text-sm">
                    View Schedule
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="text-center bg-elegant-gradient text-white rounded-2xl p-12">
          <h2 className="text-4xl font-elegant font-bold mb-6">
            Ready to Begin Your Dance Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join Northwest Ballet Academy and discover the joy of dance in a supportive, professional environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-ballet-burgundy px-8 py-4 rounded-xl font-semibold hover:bg-ballet-light transition-all duration-300 transform hover:scale-105">
              Register for Classes
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-ballet-burgundy transition-all duration-300">
              Schedule a Tour
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-ballet-burgundy transition-all duration-300">
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Academy;
